import { useState, useRef, useEffect } from "react";
import { useSwipeable } from "react-swipeable";

/** Wrapper that exposes a single quick-action button on left swipe.
 *
 * Props:
 *   action — { label, icon: ReactNode, color: 'red'|'green'|'amber'|'blue', onClick: async () => any }
 *   actionWidth — px width of the action drawer (default 96)
 *   children — the card content (typically <MobileListCard />)
 *
 * If `action` is falsy, this just renders children with no swipe behaviour.
 */
const COLORS = {
    red: "var(--brand-red)",
    green: "var(--brand-success)",
    amber: "var(--brand-warning)",
    blue: "#1565c0",
};

export default function SwipeableListItem({ action, actionWidth = 96, children }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const containerRef = useRef(null);

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            if (!action || busy) return;
            // Tiny haptic tap on Android/iPhone web (no-op on desktop)
            if (typeof navigator !== "undefined" && navigator.vibrate) {
                try { navigator.vibrate(10); } catch (_) { /* ignore */ }
            }
            setOpen(true);
        },
        onSwipedRight: () => setOpen(false),
        trackTouch: true,
        trackMouse: false,
        delta: 30,
        preventScrollOnSwipe: true,
    });

    // Tap outside collapses the revealed action
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("touchstart", onDocClick, { passive: true });
        document.addEventListener("mousedown", onDocClick);
        return () => {
            document.removeEventListener("touchstart", onDocClick);
            document.removeEventListener("mousedown", onDocClick);
        };
    }, [open]);

    if (!action) return children;

    const handleAction = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await action.onClick();
            setOpen(false);
        } finally {
            setBusy(false);
        }
    };

    const palette = COLORS[action.color] || COLORS.blue;

    return (
        <div
            ref={containerRef}
            className="relative overflow-hidden"
            style={{ borderRadius: 10 }}
            data-testid="swipeable-item"
        >
            {/* Action drawer (behind the card) */}
            <button
                type="button"
                onClick={handleAction}
                disabled={busy}
                className="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-center gap-1 text-white font-semibold text-xs"
                style={{
                    width: actionWidth,
                    background: palette,
                    opacity: open ? 1 : 0.85,
                }}
                aria-label={action.label}
                data-testid={`swipe-action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {action.icon}
                <span>{busy ? "…" : action.label}</span>
            </button>
            {/* Sliding foreground card */}
            <div
                {...handlers}
                className="relative"
                style={{
                    transform: open ? `translateX(-${actionWidth}px)` : "translateX(0)",
                    transition: "transform 180ms ease",
                    background: "var(--brand-bg, #fff)",
                }}
            >
                {children}
            </div>
        </div>
    );
}
